import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Truck, Package, Copy, Camera, Loader2, ExternalLink, User, MapPin } from "lucide-react";
import { toast } from "sonner";

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (sid, pid) => `${backendUrl}/api/shipments/${sid}/photos/${pid}/download`;

export default function CustomerShippingTab({ customerId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("kargo");
  const [detail, setDetail] = useState(null);

  const load = () => {
    if (!customerId) return;
    setLoading(true);
    api.get(`/shipments/customer/${customerId}`)
      .then(({ data }) => setRows(data || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [customerId]);

  const kargo = useMemo(() => rows.filter((r) => r.delivery_mode === "kargo"), [rows]);
  const elden = useMemo(() => rows.filter((r) => r.delivery_mode === "elden"), [rows]);

  return (
    <div className="space-y-4" data-testid="customer-shipping-tab">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-900 border border-slate-800 rounded-sm">
          <TabsTrigger data-testid="cust-ship-tab-kargo" value="kargo" className="rounded-sm">
            <Truck className="w-3.5 h-3.5 mr-1.5" /> Kargo Teslimatları ({kargo.length})
          </TabsTrigger>
          <TabsTrigger data-testid="cust-ship-tab-elden" value="elden" className="rounded-sm">
            <Package className="w-3.5 h-3.5 mr-1.5" /> Elden Teslimatlar ({elden.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="kargo" className="mt-4">
          <ShipTable rows={kargo} mode="kargo" loading={loading} onOpen={setDetail} />
        </TabsContent>
        <TabsContent value="elden" className="mt-4">
          <ShipTable rows={elden} mode="elden" loading={loading} onOpen={setDetail} />
        </TabsContent>
      </Tabs>

      <ShipmentDetailDialog shipment={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function ShipTable({ rows, loading, mode, onOpen }) {
  if (loading) {
    return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>;
  }
  if (rows.length === 0) {
    return (
      <Card className="bg-slate-900 border-slate-800 rounded-sm p-8 text-center">
        <Package className="w-8 h-8 text-slate-700 mx-auto mb-2" />
        <div className="text-sm text-slate-400">
          Bu müşteri için henüz {mode === "kargo" ? "kargo" : "elden"} teslimat kaydı yok.
        </div>
      </Card>
    );
  }
  return (
    <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-slate-800">
            {["Tarih", "Sipariş", "Ürün", "Bedenler", "Paket", "Koli",
              mode === "kargo" ? "Kargo / Takip No" : "Teslim Alan", "Fatura No",
              "İrsaliye", ""].map((h) => (
              <TableHead key={h} className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((s) => (
            <TableRow
              key={s.id}
              className="border-slate-800 cursor-pointer hover:bg-slate-800/30"
              onClick={() => onOpen(s)}
              data-testid={`cust-ship-row-${s.id}`}
            >
              <TableCell className="text-xs text-slate-400 font-mono whitespace-nowrap">
                {s.signed_at ? new Date(s.signed_at).toLocaleDateString("tr-TR") : "—"}
              </TableCell>
              <TableCell className="text-xs font-mono text-blue-400 whitespace-nowrap">{s.order_no}</TableCell>
              <TableCell className="text-sm text-slate-200 max-w-[220px] truncate">{s.product_summary || "—"}</TableCell>
              <TableCell className="text-xs font-mono text-slate-400 max-w-[140px] truncate">{s.size_summary || "—"}</TableCell>
              <TableCell className="text-xs font-mono text-slate-200 text-right">{s.package_count || 0}</TableCell>
              <TableCell className="text-xs font-mono text-slate-200 text-right">{s.box_count || 0}</TableCell>
              <TableCell className="text-xs text-slate-200 whitespace-nowrap">
                {mode === "kargo" ? (
                  <div className="flex items-center gap-1.5">
                    <span>{s.courier_name || "—"}</span>
                    {s.tracking_no && (
                      <>
                        <span className="text-slate-600">·</span>
                        <span className="font-mono">{s.tracking_no}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(s.tracking_no); toast.success("Kopyalandı"); }}
                          className="text-slate-500 hover:text-blue-400"
                          title="Kopyala"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <span>{s.signer_name || "—"}{s.signer_role ? <span className="text-slate-500"> ({s.signer_role})</span> : null}</span>
                )}
              </TableCell>
              <TableCell className="text-xs text-slate-300 font-mono whitespace-nowrap">{s.invoice_no || "—"}</TableCell>
              <TableCell className="text-xs text-slate-300 font-mono whitespace-nowrap">{s.waybill_no || "—"}</TableCell>
              <TableCell className="text-right">
                <ExternalLink className="w-3.5 h-3.5 text-slate-500 inline-block" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function ShipmentDetailDialog({ shipment, onClose }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!shipment) { setPhotos([]); return; }
    setLoading(true);
    api.get(`/shipments/${shipment.id}/photos`)
      .then(({ data }) => setPhotos(data || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, [shipment]);

  if (!shipment) return null;

  const docPhotos = photos.filter((p) => p.kind === "document");
  const cargoPhotos = photos.filter((p) => p.kind === "cargo");
  const isKargo = shipment.delivery_mode === "kargo";

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-50">
            {isKargo ? <Truck className="w-5 h-5 text-blue-400" /> : <Package className="w-5 h-5 text-emerald-400" />}
            {isKargo ? "Kargo Teslimatı" : "Elden Teslimat"} · <span className="font-mono text-blue-400">{shipment.order_no}</span>
            <Badge className={isKargo ? "bg-blue-950/40 border border-blue-900 text-blue-300 rounded-sm text-[10px] uppercase" : "bg-emerald-950/40 border border-emerald-900 text-emerald-300 rounded-sm text-[10px] uppercase"}>
              {isKargo ? "Kargoda" : "Teslim"}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-xs font-mono">
          <Info label="Sevk Tarihi" value={shipment.signed_at ? new Date(shipment.signed_at).toLocaleString("tr-TR") : "—"} />
          <Info label="Fatura Başlığı" value={shipment.customer_invoice_title || shipment.customer_name} />
          <Info label="Ürün" value={shipment.product_summary} />
          <Info label="Beden Özeti" value={shipment.size_summary} />
          <Info label="Renk" value={shipment.color} />
          <Info label="Paket / Koli" value={`${shipment.package_count || 0} paket · ${shipment.box_count || 0} koli`} />
          <Info label="Hazırlayan" value={shipment.prepared_by} />
          <Info label="İrsaliye No" value={shipment.waybill_no} />
          <Info label="Fatura No" value={shipment.invoice_no} />
          {isKargo ? (
            <>
              <Info label="Kargo Firması" value={shipment.courier_name} />
              <Info label="Takip No" value={shipment.tracking_no} copyable />
              {shipment.tracking_url && <Info label="Takip Linki" value={<a href={shipment.tracking_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">Aç ↗</a>} />}
            </>
          ) : (
            <>
              <Info label="Teslim Alan" icon={<User className="w-3 h-3" />} value={`${shipment.signer_name || ""}${shipment.signer_role ? ` (${shipment.signer_role})` : ""}`} />
              {(shipment.latitude || shipment.longitude) && (
                <Info label="GPS Konum" icon={<MapPin className="w-3 h-3" />} value={
                  <a
                    href={`https://www.google.com/maps?q=${shipment.latitude},${shipment.longitude}`}
                    target="_blank" rel="noreferrer"
                    className="text-blue-400 hover:underline"
                  >Haritada Aç ↗</a>
                } />
              )}
            </>
          )}
        </div>

        {shipment.notes && (
          <div className="text-xs text-slate-300 bg-slate-900/60 border border-slate-800 rounded-sm p-3 whitespace-pre-line">
            <span className="text-slate-500 uppercase tracking-widest font-mono mr-2">Not:</span>
            {shipment.notes}
          </div>
        )}

        {shipment.signature_data_url && (
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Teslim İmzası</div>
            <img src={shipment.signature_data_url} alt="Sevkiyat imzası" className="max-h-32 bg-white border border-slate-800 rounded-sm p-2" />
          </div>
        )}

        <div className="border-t border-slate-800 pt-4 space-y-4">
          <div className="text-xs uppercase tracking-widest font-mono text-blue-300 flex items-center gap-2">
            <Camera className="w-3.5 h-3.5" /> Sevk Evrağı & Kargo Fotoğrafları
          </div>
          {loading && <div className="text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Fotoğraflar yükleniyor…</div>}

          {docPhotos.length > 0 && (
            <div>
              <div className="text-[10px] font-mono text-slate-500 mb-1.5">Sevk Evrağı ({docPhotos.length})</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {docPhotos.map((p) => (
                  <a key={p.id} href={photoUrl(shipment.id, p.id)} target="_blank" rel="noreferrer" className="block bg-slate-950 border border-slate-800 rounded-sm overflow-hidden aspect-square hover:border-blue-800">
                    <img src={photoUrl(shipment.id, p.id)} alt="doc" className="w-full h-full object-cover" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {cargoPhotos.length > 0 && (
            <div>
              <div className="text-[10px] font-mono text-slate-500 mb-1.5">Kargo Fotoğrafları ({cargoPhotos.length})</div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {cargoPhotos.map((p) => (
                  <a key={p.id} href={photoUrl(shipment.id, p.id)} target="_blank" rel="noreferrer" className="block bg-slate-950 border border-slate-800 rounded-sm overflow-hidden aspect-square hover:border-blue-800">
                    <img src={photoUrl(shipment.id, p.id)} alt="cargo" className="w-full h-full object-cover" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {!loading && docPhotos.length === 0 && cargoPhotos.length === 0 && (
            <div className="text-xs text-slate-500">Bu sevkiyat için henüz fotoğraf yok. Sevkiyat kaydından ekleyebilirsiniz.</div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
          <Button variant="outline" onClick={onClose} className="border-slate-800 bg-slate-900 text-slate-300 hover:bg-slate-800 rounded-sm">Kapat</Button>
          <a
            href={`/sevkiyat`}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase tracking-wider transition-colors"
          >
            Sevkiyat Sayfasında Aç <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, icon, copyable }) {
  if (!value) return null;
  return (
    <div className="space-y-0.5">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1">{icon}{label}</div>
      <div className="text-slate-200 flex items-center gap-1.5">
        <span className="truncate">{value}</span>
        {copyable && typeof value === "string" && (
          <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Kopyalandı"); }}
            className="text-slate-500 hover:text-blue-400 shrink-0">
            <Copy className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}
